import { differenceInCalendarMonths } from 'date-fns';
import { useMemo } from 'react';
import type { Referral } from '@/common/hooks/referrals/types';
import { useReferral } from '@/common/hooks/referrals/useReferral';
import { formatDate } from '@/components/DatePicker/helpers';

export type ReferralAgreement = Pick<
  Referral,
  'agreementStatus' | 'commissionType' | 'commissionValue'
> & {
  startDate: Date | null;
  endDate: Date | null;
  termLengthMonths: number;
};

export type CommissionType = NonNullable<ReferralAgreement['commissionType']>;

const DEFAULT_TERM_LENGTH = 18;

const calculateTermLength = (startDate: Date | null, endDate: Date | null) => {
  if (!startDate || !endDate) {
    return DEFAULT_TERM_LENGTH;
  }
  return differenceInCalendarMonths(endDate, startDate);
};

export function useReferralAgreement(referralId: string) {
  const { interaction, ...rest } = useReferral(referralId);

  const agreement = useMemo(() => {
    if (interaction) {
      const startDate = formatDate(interaction.startDate);
      const endDate = formatDate(interaction.endDate);

      return {
        agreementStatus: interaction.agreementStatus,
        startDate,
        endDate,
        commissionType: interaction.commissionType,
        commissionValue: interaction.commissionValue,
        termLengthMonths: calculateTermLength(startDate, endDate),
      } satisfies ReferralAgreement;
    }

    return undefined;
  }, [interaction]);

  return {
    agreement,
    ...rest,
  };
}
